// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import { type WorkOrder, useEvolvePaginatedGet, type Shipment } from '@evolvemep/api-ts';
import { Flex, getDefaultZIndex, Title } from '@mantine/core';
import { useForm } from '@mantine/form';

import { FormModal } from 'components/Mantine/Modals/FormModal';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import type { WithNullable } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import {
  AddShipmentItemsForm,
  type ShipmentItemsFormValues,
} from 'modules/Shop/Shipments/ShipmentDetail/AddShipmentItemsModal/AddShipmentItemsForm';
import { useShipmentStatusFilterIds } from 'modules/Shop/Shipments/ShipmentDetail/useCanEditShipment';
import { NewShipmentModal } from 'modules/Shop/Shipments/ShipmentsPage/NewShipmentModal';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

type Props = {
  workOrder: WorkOrder;
  /**
   * Items to pre-select when the modal opens. Pass a single item for the per-item entry point,
   * or all eligible items for the section-level entry point.
   */
  initialValues?: ShipmentItemsFormValues;
  opened: boolean;
  onClose: () => void;
  onItemsAdded: () => void;
};

const SelectShippingOrder = ({
  value,
  onChange,
  workOrder,
}: {
  value: Shipment | null;
  onChange: (shipment: Shipment | null) => void;
  workOrder: WorkOrder;
}) => {
  const openShipmentStateIds = useShipmentStatusFilterIds('addEditDeleteItems');
  const defaultShipmentOpts = useMemo(
    () => ({
      lazy: isNil(openShipmentStateIds),
      defaultConfig: { params: { shipmentStateIds: openShipmentStateIds ?? [], orderBy: 'createdOn:desc' as const } },
    }),
    [openShipmentStateIds],
  );
  const shipmentsPaginatedGet = useEvolvePaginatedGet('shop/shipment', defaultShipmentOpts);
  const { setDefaultOpts: setShipmentsDefaultOpts, reset: onNewShipmentCreated } = shipmentsPaginatedGet;
  useEffect(() => setShipmentsDefaultOpts(defaultShipmentOpts), [defaultShipmentOpts, setShipmentsDefaultOpts]);

  return (
    <SearchableSelect
      label="Shipping Order"
      required
      paginatedGet={shipmentsPaginatedGet}
      idKey="shipmentId"
      getItemLabel={(s) => `${s.identifier}: ${s.name}`}
      value={value}
      onChange={onChange}
      creatable
      w={250}
      createLabel="Add new shipping order"
      creationModal={(name, props) => (
        <NewShipmentModal
          {...props}
          keepMounted={false}
          defaultValues={{
            project: { projectId: workOrder.projectId, projectName: workOrder.projectName },
            facility: { facilityId: workOrder.facilityId, facilityName: workOrder.facilityName },
            name,
          }}
          onShipmentCreated={(shipment) => {
            onChange(shipment);
            onNewShipmentCreated();
          }}
        />
      )}
    />
  );
};

type ValidatedFormBody = {
  shipment: Shipment;
};
type FormBody = WithNullable<ValidatedFormBody>;

const ShipmentSelectFields = ({
  workOrder,
  onShipmentSelected,
}: Pick<Props, 'workOrder'> & { onShipmentSelected: (s: Shipment) => void }) => {
  const form = useForm<FormBody>({
    initialValues: {
      shipment: null,
    },
    validate: {
      shipment: isNil,
    },
  });

  const onSubmit = async (rawValues: typeof form.values) => {
    const values = rawValues as ValidatedFormBody;
    onShipmentSelected(values.shipment);
  };

  useFormPopover(form, onSubmit);

  return (
    <SelectShippingOrder
      value={form.values.shipment}
      onChange={(shipment) => form.setFieldValue('shipment', shipment)}
      workOrder={workOrder}
    />
  );
};

const ShipmentSelectPopover = ({
  workOrder,
  opened,
  onClose,
  onShipmentSelected,
}: Pick<Props, 'workOrder' | 'opened' | 'onClose'> & { onShipmentSelected: (s: Shipment) => void }) => (
  <FormPopover
    opened={opened}
    // `onClose` closes the entire add-to-shipment flow; confirming instead advances to the
    // modal step (the parent flips `opened` once a shipment is selected), so never auto-close.
    closeOnConfirm={false}
    onClose={onClose}
    confirmationText="Continue"
    zIndex={getDefaultZIndex('modal')}
  >
    <ShipmentSelectFields workOrder={workOrder} onShipmentSelected={onShipmentSelected} />
  </FormPopover>
);

export const AddToShipmentFromWorkOrderModal = ({ workOrder, initialValues, opened, onClose, onItemsAdded }: Props) => {
  const [shipment, setShipment] = useState<Shipment | null>(null);

  useEffect(() => {
    if (!opened) setShipment(null);
  }, [opened]);
  return (
    <>
      <ShipmentSelectPopover
        opened={opened && isNil(shipment)}
        onClose={onClose}
        workOrder={workOrder}
        onShipmentSelected={setShipment}
      />
      <FormModal
        opened={opened && isNotNil(shipment)}
        onClose={onClose}
        miniHeader={getDisplayNameWO(workOrder)}
        title={
          <Flex gap="md" align="center" justify="space-between" flex={1}>
            <Title order={3}>Add work items</Title>
            <SelectShippingOrder value={shipment} onChange={setShipment} workOrder={workOrder} />
          </Flex>
        }
        tall
        size="xl"
        type="submit"
        confirmationText="Add items"
        startDirty
      >
        {isNotNil(shipment) && (
          <AddShipmentItemsForm
            key={shipment.shipmentId}
            shipment={shipment}
            onItemsAdded={onItemsAdded}
            initialValues={initialValues}
            workOrder={workOrder}
          />
        )}
      </FormModal>
    </>
  );
};
